import { safeRandomUUID } from "@/lib/utils"
import { supabase } from "@/lib/supabase"
import { toast } from "@/hooks/use-toast"
import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from "react"

type ProfileEngagementReturn = {
  profileId: string
  likes: number
  shares: number
  hasLiked: boolean
  likeBusy: boolean
  shareBusy: boolean
  like: () => Promise<void>
  share: () => Promise<boolean>
  refresh: () => Promise<void>
}

function isValidProfileId(profileId: string): boolean {
  const id = String(profileId || "")
  if (!id) return false
  if (id.length > 240) return false
  if (!/^pri:[a-z0-9:_-]+$/i.test(id)) return false
  return true
}

function nowMs() {
  return Date.now()
}

function rateLimited(lastAtRef: MutableRefObject<number>, minIntervalMs: number): boolean {
  const t = nowMs()
  if (t - lastAtRef.current < minIntervalMs) return true
  lastAtRef.current = t
  return false
}

export function useProfileEngagement(profileId: string, userId?: string | null): ProfileEngagementReturn {
  const pid = String(profileId || "")
  const uid = userId ?? null

  const [likes, setLikes] = useState(0)
  const [shares, setShares] = useState(0)
  const [hasLiked, setHasLiked] = useState(false)
  const [likeBusy, setLikeBusy] = useState(false)
  const [shareBusy, setShareBusy] = useState(false)

  const likeLastAtRef = useRef(0)
  const shareLastAtRef = useRef(0)

  const canQuery = useMemo(() => isValidProfileId(pid), [pid])

  const refresh = useCallback(async () => {
    if (!canQuery) return

    const likeCountReq = supabase
      .from("profile_engagements")
      .select("id")
      .eq("profile_id", pid)
      .eq("action_type", "like")

    const shareCountReq = supabase
      .from("profile_shares")
      .select("id")
      .eq("profile_id", pid)

    const likedReq = uid
      ? supabase
        .from("profile_engagements")
        .select("id")
        .eq("profile_id", pid)
        .eq("user_id", uid)
        .eq("action_type", "like")
        .maybeSingle()
      : null

    try {
      const [likeCountRes, shareCountRes, likedRes] = await Promise.all([
        likeCountReq,
        shareCountReq,
        likedReq ?? Promise.resolve(null),
      ])

      if (!likeCountRes.error) setLikes(Array.isArray(likeCountRes.data) ? likeCountRes.data.length : 0)
      if (!shareCountRes.error) setShares(Array.isArray(shareCountRes.data) ? shareCountRes.data.length : 0)

      if (!uid) {
        setHasLiked(false)
        return
      }
      if (likedRes && !likedRes.error) setHasLiked(Boolean(likedRes.data))
    } catch (error) {
      console.error("[useProfileEngagement] refresh failed", { profileId: pid, userId: uid, error })
    }
  }, [canQuery, pid, uid])

  useEffect(() => {
    refresh().catch((error) => {
      console.error("[useProfileEngagement] initial refresh failed", { profileId: pid, userId: uid, error })
    })
  }, [refresh])

  useEffect(() => {
    if (!canQuery) return

    const likesChannel = supabase
      .channel(`profile_engagements:${pid}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "profile_engagements", filter: `profile_id=eq.${pid}` },
        () => {
          refresh().catch((error) => {
            console.error("[useProfileEngagement] realtime refresh failed", { profileId: pid, userId: uid, error })
          })
        },
      )
      .subscribe()

    return () => {
      supabase.removeChannel(likesChannel)
    }
  }, [canQuery, pid, refresh])

  const like = useCallback(async () => {
    if (!canQuery) return
    if (likeBusy) return
    if (rateLimited(likeLastAtRef, 6000)) return

    if (!uid) {
      return
    }

    setLikeBusy(true)
    try {
      const { data: existing, error: fetchError } = await supabase
        .from("profile_engagements")
        .select("id")
        .eq("profile_id", pid)
        .eq("user_id", uid)
        .eq("action_type", "like")
        .maybeSingle()

      if (fetchError) {
        toast({ variant: "destructive", description: "Like failed" })
        return
      }

      if (existing?.id) {
        console.log("[profile_engagements] BEFORE_DELETE", { profile_id: pid, user_id: uid, action_type: "like", id: existing.id })
        const { error } = await supabase
          .from("profile_engagements")
          .delete()
          .eq("id", existing.id)

        if (error) {
          console.log("[profile_engagements] DELETE_ERROR", { profile_id: pid, user_id: uid, action_type: "like", id: existing.id, error })
          toast({ variant: "destructive", description: "Like failed" })
          return
        }
        console.log("[profile_engagements] AFTER_DELETE", { profile_id: pid, user_id: uid, action_type: "like", id: existing.id })
        setHasLiked(false)
      } else {
        console.log("[profile_engagements] BEFORE_INSERT", { profile_id: pid, user_id: uid, action_type: "like" })
        const { error } = await supabase.from("profile_engagements").insert({
          profile_id: pid,
          user_id: uid,
          action_type: "like",
        })

        if (error) {
          console.log("[profile_engagements] INSERT_ERROR", { profile_id: pid, user_id: uid, action_type: "like", error })
          toast({ variant: "destructive", description: "Like failed" })
          return
        }
        console.log("[profile_engagements] AFTER_INSERT", { profile_id: pid, user_id: uid, action_type: "like" })
        setHasLiked(true)
      }

      await refresh()
    } finally {
      setLikeBusy(false)
    }
  }, [canQuery, likeBusy, pid, refresh, uid])

  const share = useCallback(async () => {
    if (!canQuery) return false
    if (shareBusy) return false
    if (rateLimited(shareLastAtRef, 6000)) return false

    setShareBusy(true)
    try {
      const shareId = safeRandomUUID()
      const shareLink = `${window.location.origin}/profile/${encodeURIComponent(pid)}?share_id=${shareId}`

      console.log("[profile_shares] BEFORE_INSERT", { share_id: shareId, profile_id: pid, sharer_user_id: uid })
      const { error: insertError } = await supabase.from("profile_shares").insert({
        share_id: shareId,
        profile_id: pid,
        sharer_user_id: uid,
      })

      if (insertError) {
        console.log("[profile_shares] INSERT_ERROR", { share_id: shareId, profile_id: pid, sharer_user_id: uid, error: insertError })
        toast({ variant: "destructive", description: "Share failed" })
        return false
      }
      console.log("[profile_shares] AFTER_INSERT", { share_id: shareId, profile_id: pid, sharer_user_id: uid })

      const message = `देखिए अपनी डिजिटल पंचायत 👇\n\n${shareLink}`
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`

      window.open(whatsappUrl, "_blank")

      try {
        const copyMessage = `देखिए अपनी डिजिटल पंचायत 👇\n\n${shareLink}`
        await navigator.clipboard.writeText(copyMessage)
        alert("Link copied:\n\n" + copyMessage)
        return true
      } catch {
        toast({ variant: "destructive", description: "Copy failed" })
        return false
      }
    } finally {
      setShareBusy(false)
    }
  }, [canQuery, pid, shareBusy, uid])

  return {
    profileId: pid,
    likes,
    shares,
    hasLiked,
    likeBusy,
    shareBusy,
    like,
    share,
    refresh,
  }
}

export async function trackShareOpen(shareId: string): Promise<void> {
  const sid = String(shareId || "")
  if (!sid) return
  if (sid.length > 128) return

  const FingerprintJS = (await import("@fingerprintjs/fingerprintjs")).default
  const fp = await FingerprintJS.load()
  const result = await fp.get()
  const fingerprint = result.visitorId

  console.log("[share_analytics] BEFORE_INSERT", { share_id: sid, device_fingerprint: fingerprint })
  const { error } = await supabase.from("share_analytics").insert({
    share_id: sid,
    device_fingerprint: fingerprint,
    user_agent: navigator.userAgent,
  })

  if (!error) {
    console.log("[share_analytics] AFTER_INSERT", { share_id: sid, device_fingerprint: fingerprint })
    return
  }
  const code = typeof (error as unknown as { code?: unknown }).code === "string" ? (error as unknown as { code: string }).code : ""
  if (code === "23505" || code === "409") {
    console.log("Duplicate device - already counted")
    return
  }
  console.log("[share_analytics] INSERT_ERROR", { share_id: sid, device_fingerprint: fingerprint, error })
}
